import {
	ArrowLeftOutlined,
	BookOutlined,
	SaveOutlined,
	SearchOutlined,
} from '@ant-design/icons'
import {
	Button,
	Input,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Tabs,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ROUTE } from '../../utils/constant'

const PAGE_SIZE = 50

// ---- helpers ----------------------------------------------------------------

function langFieldKey(lang) {
	if (lang === 'en') return 'nameEng'
	if (lang === 'vi') return 'nameVi'
	return `name${lang.charAt(0).toUpperCase()}${lang.slice(1)}`
}

function getCategoryLangFieldKey(lang) {
	if (lang === 'en') return 'categoryNameEng'
	if (lang === 'vi') return 'categoryNameVi'
	return `categoryName${lang.charAt(0).toUpperCase()}${lang.slice(1)}`
}

/** Flatten nested CMS course payload → table row shape. */
function mapCourseItem(item, lang) {
	const byLang = {}
	for (const t of item?.translations || []) {
		if (t?.language) byLang[t.language] = t.name ?? ''
	}
	return {
		id: item.folderID,
		nameEng: byLang.en ?? '',
		[langFieldKey(lang)]: byLang[lang] ?? '',
		status: item.status || {},
		categoryName: item.categoryName ?? '',
	}
}

/** Flatten nested CMS category payload → table row shape. */
function mapCategoryItem(item, lang) {
	const byLang = {}
	for (const t of item?.translations || []) {
		if (t?.language) byLang[t.language] = t.name ?? ''
	}
	return {
		id: item.categoryID,
		nameEng: byLang.en ?? '',
		[getCategoryLangFieldKey(lang)]: byLang[lang] ?? '',
		status: item.status || {},
	}
}

// ---- Course tab -------------------------------------------------------------

const CourseTab = ({ lang, langName, isRtl }) => {
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(new Set())
	const [query, setQuery] = useState('')
	const [page, setPage] = useState(1)
	const [localEdits, setLocalEdits] = useState({}) // { [folderID]: newName }
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		return () => { mountedRef.current = false }
	}, [])

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.i18nCourseApi.listCourses({
				q: query.trim() || undefined,
				pageNum: page,
				pageSize: PAGE_SIZE,
			})
			if (!mountedRef.current) return
			const raw = Array.isArray(rs?.data) ? rs.data : []
			setItems(raw.map((item) => mapCourseItem(item, lang)))
			setTotal(Number(rs?.total) || 0)
			setLocalEdits({})
		} catch (err) {
			message.error(err?.message || 'Could not load courses.')
		} finally {
			if (mountedRef.current) setLoading(false)
		}
	}, [query, page, lang])

	useEffect(() => { load() }, [load])

	const handleEdit = (id, value) => {
		setLocalEdits((prev) => ({ ...prev, [id]: value }))
	}

	const handleSaveOne = async (record) => {
		const newName = localEdits[record.id]?.trim()
		if (!newName) {
			message.warning('Name cannot be empty.')
			return
		}
		if (!record.id) {
			message.error('Missing course id.')
			return
		}
		setSaving((prev) => new Set(prev).add(record.id))
		try {
			await api.i18nCourseApi.upsertCourseTranslation(record.id, lang, newName)
			message.success(`Saved "${newName}" for ${lang}.`)
			// Update local row immediately
			setItems((prev) =>
				prev.map((r) =>
					r.id === record.id
						? { ...r, [langFieldKey(lang)]: newName }
						: r
				)
			)
			setLocalEdits((prev) => {
				const next = { ...prev }
				delete next[record.id]
				return next
			})
		} catch (err) {
			message.error(err?.message || 'Save failed.')
		} finally {
			setSaving((prev) => {
				const next = new Set(prev)
				next.delete(record.id)
				return next
			})
		}
	}

	const langKey = langFieldKey(lang)

	const columns = [
		{
			title: 'English name',
			dataIndex: 'nameEng',
			width: '35%',
		},
		{
			title: `${langName} name`,
			width: '45%',
			render: (_, record) => {
				const currentSaved = record[langKey] ?? ''
				const editing = localEdits[record.id] !== undefined
				const displayVal = editing ? localEdits[record.id] : currentSaved
				return (
					<Input
						size="small"
						value={displayVal}
						placeholder={`Enter ${langName} name…`}
						style={isRtl ? { direction: 'rtl' } : undefined}
						onChange={(e) => handleEdit(record.id, e.target.value)}
						suffix={
							editing ? (
								<Tag color="warning" style={{ margin: 0, fontSize: 10 }}>
									unsaved
								</Tag>
							) : currentSaved ? (
								<Tag color="success" style={{ margin: 0, fontSize: 10 }}>
									✓
								</Tag>
							) : null
						}
					/>
				)
			},
		},
		{
			title: '',
			width: 80,
			align: 'center',
			render: (_, record) => (
				<Tooltip title={`Save ${langName} name`}>
					<Button
						size="small"
						type="primary"
						icon={<SaveOutlined />}
						loading={saving.has(record.id)}
						disabled={localEdits[record.id] === undefined}
						onClick={() => handleSaveOne(record)}
					/>
				</Tooltip>
			),
		},
	]

	return (
		<div>
			<div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
				<Input
					prefix={<SearchOutlined />}
					placeholder="Search by English name…"
					allowClear
					style={{ width: 320 }}
					value={query}
					onChange={(e) => { setQuery(e.target.value); setPage(1) }}
				/>
				<Button icon={<SaveOutlined />} onClick={load} loading={loading}>
					Reload
				</Button>
			</div>
			<Spin spinning={loading}>
				<Table
					rowKey="id"
					columns={columns}
					dataSource={items}
					pagination={{
						current: page,
						pageSize: PAGE_SIZE,
						total,
						onChange: (p) => setPage(p),
						showTotal: (t) => `${t} courses`,
					}}
					size="small"
					bordered
				/>
			</Spin>
		</div>
	)
}

// ---- Category tab -----------------------------------------------------------

const CategoryTab = ({ lang, langName, isRtl }) => {
	const [items, setItems] = useState([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(new Set())
	const [localEdits, setLocalEdits] = useState({})
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		return () => { mountedRef.current = false }
	}, [])

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.i18nCourseApi.listCategories()
			if (!mountedRef.current) return
			const raw = Array.isArray(rs?.data) ? rs.data : []
			setItems(raw.map((item) => mapCategoryItem(item, lang)))
			setLocalEdits({})
		} catch (err) {
			message.error(err?.message || 'Could not load categories.')
		} finally {
			if (mountedRef.current) setLoading(false)
		}
	}, [lang])

	useEffect(() => { load() }, [load])

	const handleEdit = (id, value) => {
		setLocalEdits((prev) => ({ ...prev, [id]: value }))
	}

	const handleSaveOne = async (record) => {
		const newName = localEdits[record.id]?.trim()
		if (!newName) { message.warning('Name cannot be empty.'); return }
		if (!record.id) { message.error('Missing category id.'); return }
		setSaving((prev) => new Set(prev).add(record.id))
		try {
			await api.i18nCourseApi.upsertCategoryTranslation(record.id, lang, newName)
			message.success(`Saved.`)
			const key = getCategoryLangFieldKey(lang)
			setItems((prev) =>
				prev.map((r) => r.id === record.id ? { ...r, [key]: newName } : r)
			)
			setLocalEdits((prev) => { const n = { ...prev }; delete n[record.id]; return n })
		} catch (err) {
			message.error(err?.message || 'Save failed.')
		} finally {
			setSaving((prev) => { const n = new Set(prev); n.delete(record.id); return n })
		}
	}

	const catLangKey = getCategoryLangFieldKey(lang)

	const columns = [
		{ title: 'English name', dataIndex: 'nameEng', width: '40%' },
		{
			title: `${langName} name`,
			width: '50%',
			render: (_, record) => {
				const currentSaved = record[catLangKey] ?? ''
				const editing = localEdits[record.id] !== undefined
				const displayVal = editing ? localEdits[record.id] : currentSaved
				return (
					<Input
						size="small"
						value={displayVal}
						placeholder={`Enter ${langName} name…`}
						style={isRtl ? { direction: 'rtl' } : undefined}
						onChange={(e) => handleEdit(record.id, e.target.value)}
						suffix={
							editing ? (
								<Tag color="warning" style={{ margin: 0, fontSize: 10 }}>unsaved</Tag>
							) : currentSaved ? (
								<Tag color="success" style={{ margin: 0, fontSize: 10 }}>✓</Tag>
							) : null
						}
					/>
				)
			},
		},
		{
			title: '',
			width: 80,
			align: 'center',
			render: (_, record) => (
				<Button
					size="small"
					type="primary"
					icon={<SaveOutlined />}
					loading={saving.has(record.id)}
					disabled={localEdits[record.id] === undefined}
					onClick={() => handleSaveOne(record)}
				/>
			),
		},
	]

	return (
		<Spin spinning={loading}>
			<Table
				rowKey="id"
				columns={columns}
				dataSource={items}
				pagination={{ pageSize: 100, showTotal: (t) => `${t} categories` }}
				size="small"
				bordered
			/>
		</Spin>
	)
}

// ---- Main component ---------------------------------------------------------

export const I18nCourseTranslations = () => {
	const history = useHistory()
	const [languages, setLanguages] = useState([])
	const [activeLang, setActiveLang] = useState(null)
	const [loadingLangs, setLoadingLangs] = useState(false)

	useEffect(() => {
		setLoadingLangs(true)
		api.i18nLanguageApi
			.list({ activeOnly: true })
			.then((rs) => {
				const langs = Array.isArray(rs?.data) ? rs.data : []
				setLanguages(langs)
				// Default to first non-EN/VI language if available, otherwise first
				const initial =
					langs.find((l) => !['en', 'vi'].includes(l.code)) ?? langs[0]
				if (initial) setActiveLang(initial.code)
			})
			.catch(() => message.error('Could not load languages.'))
			.finally(() => setLoadingLangs(false))
	}, [])

	const currentLang = languages.find((l) => l.code === activeLang)

	return (
		<div style={{ padding: 24 }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => history.push(`${ROUTE.I18N_MANAGEMENT}${ROUTE.I18N_LANGUAGES}`)}
				>
					Languages
				</Button>
				<BookOutlined style={{ fontSize: 18 }} />
				<span style={{ fontSize: 18, fontWeight: 600 }}>Course & Category Translations</span>
				<Spin spinning={loadingLangs} size="small" />
			</div>

			<div style={{ marginBottom: 16 }}>
				<Space>
					<span>Language:</span>
					<Select
						style={{ width: 200 }}
						value={activeLang}
						onChange={setActiveLang}
						loading={loadingLangs}
						options={languages.map((l) => ({
							value: l.code,
							label: (
								<span>
									<Tag color="blue" style={{ marginRight: 4 }}>{l.code}</Tag>
									{l.name}
								</span>
							),
						}))}
					/>
				</Space>
			</div>

		{activeLang && currentLang ? (
			<Tabs
				defaultActiveKey="courses"
				items={[
					{
						key: 'courses',
						label: 'Courses',
						children: (
							<CourseTab
								key={`course-${activeLang}`}
								lang={activeLang}
								langName={currentLang.name}
								isRtl={currentLang.isRtl}
							/>
						),
					},
					{
						key: 'categories',
						label: 'Categories',
						children: (
							<CategoryTab
								key={`cat-${activeLang}`}
								lang={activeLang}
								langName={currentLang.name}
								isRtl={currentLang.isRtl}
							/>
						),
					},
				]}
			/>
		) : (
				<div style={{ color: '#888' }}>Select a language to start editing.</div>
			)}
		</div>
	)
}
